import Backbone from 'backbone'
import _ from 'lodash'
import i18n from 'scoreboard/components/i18n'
import template from 'scoreboard/datasets/edit/fields/data-type-selector.hbs'
import dataTypes from 'scoreboard/datasets/edit/data-types'
import dataSuperTypes from 'scoreboard/datasets/edit/fields/data-super-types'
import selects from 'scoreboard/components/selects'
import DATA_TYPE from 'scoreboard/datasets/model/data-type'

const DataTypeSelector = Backbone.View.extend({

	className: 'data-type-selector',

	events: {
		'change [name=dataSuperType]': 'onChangeDataSuperType',
		'change [name=dataType]': 'onChangeDataType'
	},

	/**
	 * @param options
	 * @param {string[]} [options.excludedSuperTypes]
	 * @param {string[]} [options.excludedDataTypes]
	 *
	 * @param [options.dataTypeOptionsGenerator]
	 * @param [options.initialValue]
	 */
	initialize: function(options = {}) {
		this.excludedSuperTypes = new Set(options.excludedSuperTypes || [])
		this.excludedDataTypes = new Set(options.excludedDataTypes || [])
		this.dataTypeOptionsGenerator = options.dataTypeOptionsGenerator
		this.superTypesById = _.keyBy(dataSuperTypes, 'id')

		//certain subtype are excluded in some scenarios #46341, only apply if
		//we're not already defining a dataTypeOptionsGenerator generator (that
		//takes precedence)
		if (_.isNil(this.dataTypeOptionsGenerator) && this.excludedDataTypes.size > 0) {
			this.superTypesById = _.cloneDeep(this.superTypesById)
			_.each(this.superTypesById, (type, id) => {
				//remove all the excluded dataTypes
				type.dataTypes = _.filter(type.dataTypes, dataType => !this.excludedDataTypes.has(dataType))
				//if no subtypes are remaining don't keep super type
				if (type.dataTypes.length == 0) {
					delete this.superTypesById[id]
				}
			})
		}

		this.initialValue = options.initialValue
		this.isFirstRender = true
		this.superTypesByDataType = _(dataSuperTypes)
			.flatMap(superType =>
				superType.dataTypes.map(dataType => [dataType, superType.id]))
			.fromPairs()
			.value()
	},

	render: function() {
		this.$el.empty().append(template())
		this.renderDataSuperTypeDropdown()
		this.renderDataTypeDropdown()
		this.isFirstRender = false
	},

	renderDataSuperTypeDropdown: function() {
		const options = dataSuperTypes
			.filter(it => !this.excludedSuperTypes.has(it.id))
			// If all data types under it are excluded, don't bother with it
			.filter(it => it.dataTypes.find(type => !this.excludedDataTypes.has(type)))
			.map(superType => ({
				id: superType.id,
				label: i18n.get(superType.label),
				group: i18n.get(superType.group),
				selected: _.includes(superType.dataTypes, this.getDataType()),
				dataset: { icon: superType.icon }
			}))

		selects(this.$('[name=dataSuperType]'), {
			accessors: { 'value': 'id' },
			groupBy: 'group',
			stayOpenOnSelect: false,
			options
		})
	},

	renderDataTypeDropdown: function() {
		const dataSuperTypeId = this.$('[name=dataSuperType]').val()
		const dataType = this.initialValue || this.getDataType()
		const dataTypeOptions = this.dataTypeOptionsGenerator ?
			this.dataTypeOptionsGenerator(this.getDataSuperType()) : dataTypes
		const options = _(dataTypeOptions)
				.filter(dataType => {
					const superType = this.superTypesById[dataSuperTypeId]
					return superType && _.includes(superType.dataTypes, dataType.id)
				})
				.map(it => _.extend({}, it, {
					label: i18n.get(it.label),
					selected: it.id === dataType,
					dataset: { icon: it.icon }
				})).value()
		const el = this.$('[name=dataType]')
		selects(el, {
			accessors: { 'value': 'id' },
			stayOpenOnSelect: false,
			options
		})
		// the data type we get from form-helper.js is always the old one, so use our
		// this.getDataType to determine what the current data type is
		el.data('val', function() {
			const currentDataType = this.getDataType()
			// Data super type is just a UI thing; we don't store it.
			// So if form helper comes in and writes a new dataType, we may have to
			// update the dataSuperType select to make sure it is the parent of that
			// data type. This is a case of the tail wagging the dog, since normally
			// super type drives the data type and not the other way around.
			if (arguments.length) {
				const currSuperType = this.getDataSuperType()
				const newSuperType = this.superTypesByDataType[currentDataType]
				if (!currSuperType || currSuperType.id !== newSuperType) {
					this.setDataSuperType(newSuperType)
				}

				this.setDataType(currentDataType)
			}

			return this.getDataType()
		}.bind(this))
		this._toggleShowDataTypeAndRefreshLabel()
	},

	onChangeDataSuperType: function() {
		const dataType = this.$('[name=dataType]').val()
		const dataSuperType = this.getDataSuperType()
		const typeInSuperType = _.includes(dataSuperType.dataTypes, dataType)
		if (!typeInSuperType) {
			this.setDataType(this._defaultDataType())
		}
		this.renderDataTypeDropdown()
		this._toggleShowDataTypeAndRefreshLabel()
	},

	onChangeDataType: function() {
		this.trigger('change:dataType')
	},

	_defaultDataType: function() {
		const fallbackDefaultDataType = (this.isFirstRender && !_.isNil(this.initialValue)) ?
			this.initialValue : DATA_TYPE.TEXT

		const dataSuperType = this.getDataSuperType()
		if (_.isNil(dataSuperType)) return fallbackDefaultDataType

		const firstDataType = dataSuperType.dataTypes[0]
		// if the first data type is excluded, use the first non-excluded data type
		if (this.excludedDataTypes?.has(firstDataType)) {
			const validDataTypes = _.filter(dataSuperType.dataTypes, dataType =>
				!this.excludedDataTypes.has(dataType))
			// if all data types are excluded, use the fallback default
			if (_.isEmpty(validDataTypes)) {
				return fallbackDefaultDataType
			}

			return validDataTypes[0]
		}

		return firstDataType
	},

	getDataSuperType: function () {
		const selectedSuperType = this.$('[name=dataSuperType]').val()
		return selectedSuperType && this.superTypesById[selectedSuperType]
	},

	_toggleShowDataTypeAndRefreshLabel: function() {
		const dataSuperType = this.getDataSuperType()
		const superTypeSelected = !!dataSuperType
		const multipleTypes = superTypeSelected ?
			dataSuperType.dataTypes.length !== 1 : false

		if (multipleTypes) {
			const superTypeKeySegment = _.camelCase(dataSuperType.id)
			const dataTypeLabel =
				`datasets.datatype.groups.${superTypeKeySegment}.type`
			this.$('.data-type-container .label-text').html(i18n.get(dataTypeLabel))
		} else {
			this.$('.data-type-container .label-text').html('')
		}

		this.$('.data-type-container')
			.toggleClass('is-not-displayed', !multipleTypes)
	},

	getDataType: function() {
		return this.$('[name="dataType"]').val() || this._defaultDataType()
	},

	setDataType: function(newDataType) {
		this.$('[name=dataType]').val(newDataType)
		selects.update(this.$('[name=dataType]'))
		this.trigger('change:dataType')
	},

	setDataSuperType: function(newDataSuperType) {
		this.$('[name=dataSuperType]').val(newDataSuperType)
		selects.update(this.$('[name=dataSuperType]'))
		this.renderDataTypeDropdown()
	}
})

export default DataTypeSelector
DataTypeSelector.__filename__ = 'datasets/edit/fields/data-type-selector.js'
